import React, { useState, useEffect, useContext } from "react";
import { useMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/styles";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Upcoming from "./upcoming";
import UpcomingCourses from "./upcomingCourses";
import Suggestions from "./suggestions";
import Default from "../Common/default";
import { AppContext, OPEN_TOAST } from "../Context";

const useStyles = makeStyles((theme) => ({
  actions: {
    background: theme.app.background[1],
    borderBottom: `1px solid ${theme.app.borderColor}`,
    // height: "64px",
    display: "flex",
    alignItems: "center",
  },
  actionContainer: {
    margin: "0 auto",
    width: "600px",
  },
  viewContainer: {
    width: "1300px",
    margin: "0 auto 64px",
  },
  paper: {
    padding: "1rem",
  },
  tabs: {
    padding: "0 8px",
  },
  tabText: { fontSize: "0.8125rem" },
  group: {
    flexDirection: "row",
    padding: "8px",
  },
  radioButton: {
    padding: "6px",
  },
  label: {
    marginLeft: "-8px",
    marginRight: "24px",
  },
  labelText: {
    fontSize: "0.875rem",
    color: theme.app.labelColor,
  },
  buttonTextBig: {
    display: "block",
  },
  buttonTextSmall: {
    display: "none",
  },
  "@media(max-width: 1200px)": {
    viewContainer: {
      width: "77%",
    },
    actionContainer: {
      width: "77%",
    },
  },
  "@media(min-width: 1200px)": {
    viewContainer: {
      width: "77%",
    },
    actionContainer: {
      width: "77%",
    },
  },
  "@media(max-width: 1024px)": {
    viewContainer: {
      padding: "0 1rem",
      width: "100%",
    },
    actionContainer: {
      padding: "0 1rem",
      width: "100%",
    },
  },
  "@media(max-width: 600px)": {
    actionContainer: {
      justifyContent: "center",
    },
    paper: {
      padding: "0px",
    },
    buttonTextBig: {
      display: "none",
    },
    buttonTextSmall: {
      display: "block",
    },
  },
}));

const RESERVE_OCCASION = gql`
  mutation reserveOccasion($id: String, $participantId: Guid) {
    reserveOccasion(occasionId: $id, participantId: $participantId) {
      position
      failed
      errorMessage
    }
  }
`;

const Views = (props) => {
  const {
    anySuggestions,
    anyUpcoming,
    anyCourses,
    upcoming,
    upcomingCourses,
    suggestions,
    manages,
    loading,
  } = props;
  const classes = useStyles();
  const { dispatch } = useContext(AppContext);
  const [tab, setTab] = useState("upcoming");
  const [value, setValue] = useState(anyUpcoming ? "pass" : "course");
  const { language, auth } = useSelector((state) => ({
    language: state.language,
    auth: state.oidc.user !== null && !state.oidc.user.expired,
  }));
  const handleSetValue = (value) => setValue(value);
  const handleMutationSuccess = ({ reserveOccasion }) => {
    const { failed, errorMessage } = reserveOccasion;
    if (failed) {
      return dispatch({
        type: OPEN_TOAST,
        toastText: errorMessage,
        toastType: "error",
      });
    }
    dispatch({
      type: OPEN_TOAST,
      toastText: language.bookingSucceed,
      toastType: "success",
    });
  };
  const handleMutationError = (error) => console.log(error);
  const [reserveOccasion] = useMutation(RESERVE_OCCASION, {
    onCompleted: handleMutationSuccess,
    onError: handleMutationError,
  });

  useEffect(() => {
    if (anyUpcoming) {
      handleSetValue("pass");
    }
    if (anyCourses) {
      handleSetValue("course");
    }
  }, [anySuggestions, anyUpcoming, anyCourses]);

  const handleBook = (id) =>
    reserveOccasion({
      variables: { id },
    });

  const showUpcomingRadiobuttons = anyCourses && anyUpcoming;
  if (loading) {
    return <div />;
  }

  return (
    <div>
      <Default isAuth={auth} />
      {anySuggestions && (
        <div className={classes.actions}>
          <div className={classes.actionContainer}>
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              className={classes.tabs}
            >
              <Tab
                label={language.tabUpcoming}
                value="upcoming"
                classes={{ wrapper: classes.tabText }}
              />
              <Tab
                label={language.tabSuggestions}
                value="suggestions"
                classes={{ wrapper: classes.tabText }}
              />
            </Tabs>
          </div>
        </div>
      )}
      <div className={classes.viewContainer}>
        {tab === "upcoming" && (
          <div>
            {showUpcomingRadiobuttons && (
              <div>
                <RadioGroup
                  aria-label="upcoming"
                  name="upcoming"
                  className={classes.group}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                >
                  <FormControlLabel
                    value="course"
                    control={<Radio classes={{ root: classes.radioButton }} />}
                    className={classes.label}
                    classes={{ label: classes.labelText }}
                    label={language.radio_courses}
                  />
                  <FormControlLabel
                    value="pass"
                    control={<Radio classes={{ root: classes.radioButton }} />}
                    className={classes.label}
                    classes={{ label: classes.labelText }}
                    label={language.radio_occasions}
                  />
                </RadioGroup>
              </div>
            )}
            {anyUpcoming && value === "pass" && (
              <Upcoming
                upcoming={upcoming}
                manages={manages}
                book={handleBook}
                language={language}
              />
            )}
            {anyCourses && value === "course" && (
              <UpcomingCourses
                upcoming={upcomingCourses}
                manages={manages}
                book={handleBook}
                language={language}
              />
            )}
          </div>
        )}
        {tab === "suggestions" && (
          <div>
            {anySuggestions && (
              <Suggestions
                suggestions={suggestions}
                manages={manages}
                book={handleBook}
                language={language}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Views;
